/* ==========================================================================
   Luke & Lulu — project JS

   Loaded after theme.js. Keep this additive; never edit theme.js, it is
   build output and is replaced on every Prestige update.
   ========================================================================== */

(() => {
  'use strict';

  /* ------------------------------------------------------------------------
     Drag-to-snap for horizontal carousels

     Native CSS scroll-snap only offers `mandatory` (snap to NEAREST on
     release) or `proximity` (may rest between slides). Nearest means a slide
     only advances once you have dragged past its halfway point — on a hero
     whose slides are 87% of the viewport that is a ~600px drag, which reads
     as broken.

     Swiper solves this with a commit threshold, but costs ~45KB gzipped and
     would leave two carousel systems on one page. This does the same job in
     a few lines: take over the pointer drag, and on release commit to the
     next slide if the drag passed a small distance OR was a quick flick.

     Applies to any element carrying `data-drag-snap`. Mouse only — touch
     already has native momentum scrolling, which feels right as-is.

     Add `data-drag-free` for rows that should NOT step between slides — a
     row of differently sized pills, say, where snapping would land on
     arbitrary positions. Those get the drag but keep free scrolling.

     Tune the commit distance per element with data-drag-snap-threshold="0.2".
     ---------------------------------------------------------------------- */

  const DEFAULT_THRESHOLD = 0.12; // fraction of one slide that commits
  const FLICK_VELOCITY = 0.35; // px per ms — a quick flick commits regardless
  const DRAG_TOLERANCE = 5; // px before a press counts as a drag, not a click

  function setupDragSnap(track) {
    if (track.dataset.dragSnapReady === 'true') return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    track.dataset.dragSnapReady = 'true';

    const threshold = parseFloat(track.dataset.dragSnapThreshold) || DEFAULT_THRESHOLD;

    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;
    let pointerId = null;

    const cells = () => Array.from(track.children).filter((n) => n.nodeType === 1);

    function freeze() {
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
    }

    function thaw() {
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';
    }

    function onPointerDown(event) {
      if (event.button !== 0 || event.pointerType !== 'mouse') return;
      dragging = true;
      moved = false;
      pointerId = event.pointerId;
      startX = lastX = event.clientX;
      startScroll = track.scrollLeft;
      lastTime = performance.now();
      velocity = 0;
    }

    function onPointerMove(event) {
      if (!dragging || event.pointerId !== pointerId) return;

      const dx = event.clientX - startX;

      if (!moved) {
        if (Math.abs(dx) < DRAG_TOLERANCE) return;
        moved = true;
        track.classList.add('is-dragging');
        freeze();
        try {
          track.setPointerCapture(pointerId);
        } catch (e) {
          /* capture is best-effort */
        }
      }

      const now = performance.now();
      const dt = now - lastTime;
      if (dt > 0) velocity = (event.clientX - lastX) / dt;
      lastX = event.clientX;
      lastTime = now;

      track.scrollLeft = startScroll - dx;
      event.preventDefault();
    }

    function onPointerUp(event) {
      if (!dragging) return;
      dragging = false;

      if (pointerId !== null) {
        try {
          track.releasePointerCapture(pointerId);
        } catch (e) {
          /* already released */
        }
      }
      pointerId = null;

      if (!moved) return;
      track.classList.remove('is-dragging');

      // Free rows keep wherever the drag left them.
      if (track.hasAttribute('data-drag-free')) {
        thaw();
        return;
      }

      const items = cells();
      if (items.length < 2) {
        thaw();
        return;
      }

      // Real per-cell offsets rather than one uniform step, so rows of
      // unequal-width cells (a pill row) land correctly. Read from layout,
      // not parsed from CSS, so it stays right at any breakpoint.
      const base = items[0].offsetLeft;
      const offsets = items.map((cell) => cell.offsetLeft - base);

      let current = 0;
      let nearest = Infinity;
      offsets.forEach((offset, index) => {
        const distance = Math.abs(offset - startScroll);
        if (distance < nearest) {
          nearest = distance;
          current = index;
        }
      });

      const dx = event.clientX - startX;
      // Measured against the cell being left, so the commit distance stays
      // proportional whether the cell is a 300px pill or a full-width slide.
      const reference = items[current].offsetWidth || 1;
      const committed = Math.abs(dx) > reference * threshold || Math.abs(velocity) > FLICK_VELOCITY;
      const direction = dx < 0 ? 1 : -1;
      const target = Math.max(0, Math.min(items.length - 1, current + (committed ? direction : 0)));

      // Snapping stays off until the animation finishes, otherwise the
      // browser's own snap fights the programmatic scroll mid-flight.
      const left = offsets[target];
      track.style.scrollBehavior = '';
      track.scrollTo({ left, behavior: 'smooth' });

      /* Snapping must stay off until the animation actually finishes, and
         getting that wrong is what made slides sometimes jump instead of
         glide. The drag scrolls the track by setting scrollLeft directly, so
         releasing the pointer fires its own `scrollend` — which a naive
         listener catches immediately, restores `scroll-snap-type: mandatory`
         mid-flight, and the browser snaps the rest of the way in one frame.

         Two guards: ignore any scrollend that arrives while we are not yet at
         the target, and only start listening on the next frame so the drag's
         own event has already passed. */
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        track.removeEventListener('scrollend', onScrollEnd);
        thaw();
      };

      const onScrollEnd = () => {
        if (Math.abs(track.scrollLeft - left) > 2) return;
        finish();
      };

      requestAnimationFrame(() => {
        if (!settled) track.addEventListener('scrollend', onScrollEnd);
      });

      setTimeout(finish, 800); // fallback where scrollend is unsupported
    }

    // Suppress the click that follows a drag, so dragging across a button
    // inside a slide doesn't trigger it.
    function onClick(event) {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('click', onClick, true);

    // Unconditional, not `if (moved)`. Native drag-and-drop starts on the
    // first pixel of movement — before the 5px threshold marks this a drag —
    // and once it does the browser stops sending pointermove, so the track
    // freezes until release. Cancelling it outright keeps the pointer ours.
    track.addEventListener('dragstart', (event) => event.preventDefault());
  }

  /* ------------------------------------------------------------------------
     FAQ accordion (sections/faq-with-image.liquid)

     Registered here rather than in the section, so the definition is sent
     once per page instead of being inlined with every render.

     The panel animates via grid-template-rows 0fr -> 1fr, which needs no
     height measurement and so survives content changes and font swaps.
     ---------------------------------------------------------------------- */
  if (!customElements.get('faq-accordion-item')) {
    customElements.define(
      'faq-accordion-item',
      class FaqAccordionItem extends HTMLElement {
        connectedCallback() {
          this.trigger = this.querySelector('.faq-accordion__trigger');
          this.collapse = this.querySelector('.faq-accordion__collapse');
          if (!this.trigger || !this.collapse || this.dataset.ready === 'true') return;
          this.dataset.ready = 'true';
          this.trigger.addEventListener('click', () => this.toggle());
        }

        toggle() {
          const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
          this.trigger.setAttribute('aria-expanded', String(!isOpen));
          this.collapse.classList.toggle('is-open', !isOpen);
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     Video sound toggle (sections/video-carousel.liquid)

     Delegated from the document, so it covers sections the theme editor
     re-renders without needing to re-bind.

     Unmuting one clip mutes the others — several tracks playing over each
     other is the obvious failure mode with a row of autoplaying videos.
     ---------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-video-sound]');
    if (!button) return;

    const slide = button.closest('[data-video-slide]');
    const video = slide && slide.querySelector('video');
    if (!video) return;

    const turningOn = video.muted;

    if (turningOn) {
      document.querySelectorAll('[data-video-slide] video').forEach((other) => {
        if (other === video) return;
        other.muted = true;
        const otherButton = other.closest('[data-video-slide]').querySelector('[data-video-sound]');
        if (otherButton) otherButton.setAttribute('aria-pressed', 'false');
      });
    }

    video.muted = !turningOn;
    button.setAttribute('aria-pressed', String(turningOn));

    // Autoplay may be off, or the browser may have paused it — a click is a
    // user gesture, so this is the moment playback is allowed to start.
    if (turningOn && video.paused) video.play().catch(() => {});
  });

  /* ------------------------------------------------------------------------
     Featured-centre slide

     Marks whichever cell is nearest the track's horizontal centre with
     `is-featured`, and keeps it updated as the track scrolls. CSS gives that
     class the extra height and transitions it, so the raised slide follows
     the scroll instead of being pinned to one block.

     Applies to any track with `data-featured-center`.
     ---------------------------------------------------------------------- */
  function setupFeaturedCenter(track) {
    if (track.dataset.featuredCenterReady === 'true') return;
    track.dataset.featuredCenterReady = 'true';

    let frame = null;

    const update = () => {
      frame = null;
      const items = Array.from(track.children).filter((node) => node.nodeType === 1);
      if (!items.length) return;

      const bounds = track.getBoundingClientRect();
      const centre = bounds.left + bounds.width / 2;

      let winner = null;
      let closest = Infinity;

      items.forEach((cell) => {
        const rect = cell.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - centre);
        if (distance < closest) {
          closest = distance;
          winner = cell;
        }
      });

      items.forEach((cell) => cell.classList.toggle('is-featured', cell === winner));
    };

    // rAF-throttled: scroll fires far more often than the screen repaints.
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };

    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
  }

  function init(root = document) {
    root.querySelectorAll('[data-drag-snap]').forEach(setupDragSnap);
    root.querySelectorAll('[data-featured-center]').forEach(setupFeaturedCenter);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

/* ---------------------------------------------------------------------------
   Evergreen countdown  <evergreen-countdown duration="…" storage-key="…">

   Per-visitor rather than a fixed deadline: the clock starts the first time
   someone sees it and restarts when it reaches zero, so the bar never sits at
   00:00:00 and never needs a date updating in the customiser.
   --------------------------------------------------------------------------- */
(() => {
  const UNITS = ['days', 'hours', 'minutes', 'seconds'];

  const readStore = (key) => {
    try {
      return parseInt(window.localStorage.getItem(key), 10);
    } catch (error) {
      return NaN; // private mode, or storage disabled
    }
  };

  const writeStore = (key, value) => {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (error) {
      /* falls back to a per-page-load clock */
    }
  };

  const pad = (value) => String(value).padStart(2, '0');

  class EvergreenCountdown extends HTMLElement {
    connectedCallback() {
      this.duration = parseInt(this.getAttribute('duration'), 10);
      if (!this.duration || this.duration < 1000) return;

      this.storageKey = this.getAttribute('storage-key') || 'evergreen-countdown';
      this.fields = {};
      UNITS.forEach((unit) => {
        this.fields[unit] = this.querySelector(`[data-countdown-${unit}]`);
      });

      const stored = readStore(this.storageKey);
      this.deadline = stored > Date.now() ? stored : this.restart();

      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    restart() {
      const deadline = Date.now() + this.duration;
      writeStore(this.storageKey, deadline);
      return deadline;
    }

    tick() {
      let remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        this.deadline = this.restart();
        remaining = this.duration;
      }

      const total = Math.floor(remaining / 1000);
      const values = {
        days: Math.floor(total / 86400),
        hours: Math.floor(total / 3600) % 24,
        minutes: Math.floor(total / 60) % 60,
        seconds: total % 60,
      };

      UNITS.forEach((unit) => {
        const field = this.fields[unit];
        if (!field) return;

        const next = pad(values[unit]);
        if (field.textContent !== next) field.textContent = next;
      });
    }
  }

  if (!customElements.get('evergreen-countdown')) {
    customElements.define('evergreen-countdown', EvergreenCountdown);
  }
})();
